/**
 * Que controles ofrece el visor para un estudio concreto.
 *
 * @module domain/visor
 */

export interface Ventana {
  readonly nombre: string
  readonly ancho: number
  readonly centro: number
}

export interface CaracteristicasDeLaImagen {
  readonly modalidad: string | null
  /** `RescaleType HU`, o una tomografia que lo da por supuesto. */
  readonly enHounsfield: boolean
  /** Las que declara el archivo, en orden, con su nombre si lo trae. */
  readonly ventanasDelArchivo: readonly Ventana[]
  /** Imagen ya revelada: ecografia, color o paleta. No hay ventana que ajustar. */
  readonly yaRevelada: boolean
  /** `PixelSpacing` presente: la distancia medida es la del paciente. */
  readonly calibradaAlPaciente: boolean
  readonly cortes: number
}

export interface ControlesDelVisor {
  readonly ventanas: readonly Ventana[]
  readonly ajusteDeVentana: boolean
  readonly recorrerCortes: boolean
  readonly medidaEnMilimetros: boolean
  readonly sondaEnHounsfield: boolean
}

/**
 * Ventanas clinicas por modalidad, en unidades Hounsfield.
 *
 * Solo tienen sentido donde la imagen esta en Hounsfield, que es lo que las
 * hace comparables entre equipos. El dia que cada IPS quiera las suyas, esta
 * tabla se reemplaza por lo que venga de su configuracion sin tocar el visor.
 */
const VENTANAS_EN_HOUNSFIELD: Readonly<Record<string, readonly Ventana[]>> = {
  CT: [
    { nombre: 'Hueso', ancho: 2000, centro: 300 },
    { nombre: 'Partes blandas', ancho: 400, centro: 40 },
    { nombre: 'Pulmón', ancho: 1500, centro: -600 },
    { nombre: 'Mediastino', ancho: 350, centro: 50 },
    { nombre: 'Abdomen', ancho: 400, centro: 50 },
    { nombre: 'Hígado', ancho: 150, centro: 30 },
    { nombre: 'Cerebro', ancho: 80, centro: 40 },
  ],
  PT: [{ nombre: 'Partes blandas', ancho: 400, centro: 40 }],
}

const SIN_VENTANAS: readonly Ventana[] = []

const sonLaMisma = (a: Ventana, b: Ventana): boolean =>
  Math.round(a.ancho) === Math.round(b.ancho) && Math.round(a.centro) === Math.round(b.centro)

/**
 * Las ventanas que se le ofrecen al usuario para esta imagen.
 *
 * Primero las que trae el archivo, que las puso el equipo que adquirio, y
 * despues las clinicas de la modalidad. No se repite una que ya venga.
 */
export const ventanasPara = (imagen: CaracteristicasDeLaImagen): readonly Ventana[] => {
  if (imagen.yaRevelada) return SIN_VENTANAS

  const delArchivo = imagen.ventanasDelArchivo
  if (!imagen.enHounsfield) return delArchivo

  const clinicas = VENTANAS_EN_HOUNSFIELD[imagen.modalidad ?? ''] ?? SIN_VENTANAS
  return [
    ...delArchivo,
    ...clinicas.filter((clinica) => !delArchivo.some((propia) => sonLaMisma(propia, clinica))),
  ]
}

export const controlesPara = (imagen: CaracteristicasDeLaImagen): ControlesDelVisor => ({
  ventanas: ventanasPara(imagen),
  ajusteDeVentana: !imagen.yaRevelada,
  recorrerCortes: imagen.cortes > 1,
  medidaEnMilimetros: imagen.calibradaAlPaciente,
  sondaEnHounsfield: imagen.enHounsfield,
})
